import moment from "moment";
import {
  list as listRes,
  getCurrentCountry as getCurrentCountryRes,
} from "../apis/plan";
import {
  CURRENT_USER_PLAN_LIST,
  CURRENT_USER_INFO_SIMPLE,
} from "../store/mutation-types";
import store from "../store/index";
import Vue from "vue";
import { dateFormat } from "../utils/plugin_helper";
import NP from "number-precision";
/**
 * 计算下一次重置时间
 * @param beforeDate 之前日期
 * @param afterDate 结束日期 不填写为当前日期
 */
export function computeNextMonth(beforeDate, afterDate) {
  // 获取本月重置时间
  const monthDiff = moment(afterDate).diff(moment(beforeDate), "month") + 1;
  let currentRestDate = moment(beforeDate).add(monthDiff, "M");
  // 比较本次重置时间是否大于当前时间，如果是在重置时间为本次时间，不是则推迟一个月
  if (moment(afterDate).diff(currentRestDate) >= 0) {
    currentRestDate = moment(beforeDate).add(monthDiff + 1, "M");
  }
  // 如果购买时间日期大于计算本月重置时间则变为下个月1号
  if (moment(beforeDate).date() > currentRestDate.date()) {
    currentRestDate = moment(beforeDate)
      .add(monthDiff + 1, "M")
      .date(1);
  }
  return currentRestDate;
}

/**
 * 获取当前城市
 */
export async function getCurrentCountry() {
  const { data } = await getCurrentCountryRes();
  return { country: data.country };
}

/**
 * 获取plan列表
 * @returns {Promise<*>}
 */
export async function list() {
  const { data } = await listRes();
  // 保存到vuex中
  store.commit(CURRENT_USER_PLAN_LIST, data);
  return data;
}

/**
 * 计划实体
 */
export class Plan {
  constructor(vo) {
    // 计划名称
    this.name = vo.name;
    // 计划描述信息
    this.desc = vo.desc;
    // 该计划中最低价格
    this.price = vo.best_price;
    // 用于显示的价格
    this.displayPrice = vo.best_display_price_per_month;
    // 付费方式(多种)
    this.billingCycles = vo.billing_cycle.map((bc) => new PlanCycle(bc));
    // 判断当前用户plan 是否和该条相同
    this.active = false;
    // 防止当前登录人信息还没有获取到
    if (store.getters[CURRENT_USER_INFO_SIMPLE]) {
      this.active =
        store.getters[CURRENT_USER_INFO_SIMPLE].plan.name === this.name;
    }
    // 判断是否为most popular
    this.mostPupular = vo.most_popular;
    // 2019.12.25 新增级别字段 用于判断升降级
    this.level = vo.level;
  }
}

/**
 * 计划中单个支付周期信息
 */
export class PlanCycle {
  constructor(vo) {
    // 唯一标示
    this.id = vo.id;
    // 付费描述
    this.desc = vo.billing_desc;
    // 付费周期 如果12个月 1个月
    this.interval = Number(vo.billing_interval);
    // cycle名称
    this.name = vo.display_price;
    // 总价格
    this.totalPrice = vo.display_price;
    // 每月价格
    this.pricePerMonth = vo.display_price_per_month;
    // 2019.12.14 增加对多币种支持，paypal弹出框里可能和外层展示不同
    this.paypalToalPrice = vo.display_price_in_usd;
    this.paypalPrePrice = vo.display_price_per_month_in_usd;
    this.payPalSupportCurrency = vo.does_paypal_support_currency;
    this.payPalCurrency = "USD";
    // 币种
    this.currency = vo.currency;
    // 当前币种总价格
    this.totalPriceNum = Number(vo.price);
    // 美金币种价格
    this.totalUsdPriceNum = Number(vo.price_in_usd);
    // 2018.9.29 添加 iap码
    this.playPlanId = vo.play_plan_id;
    // 2019.12.31 新增 带货币符号价格
    this.displayPrice = vo.display_price_per_month_with_interval;
  }
}

/**
 * 支付计划实体
 */
export class UserPlan {
  constructor(vo) {
    if (vo) {
      this.id = vo.plan.id;
      // 此周期结束时间
      this.currentPeriodEnd = moment(vo.current_period_end);
      // 此周期开始时间
      this.currentPeriodStart = moment(vo.current_period_start);
      // 计划名称
      this.name = vo.plan.name;
      // 价格
      this.price = vo.plan.price;
      // 包含周期的计划名称
      this.intervalName = vo.plan.name_with_interval;
      // 计划publisher最大数量
      this.premiumPublisherCount = vo.plan.premium_publisher_count;
      // 计划member最大数量
      this.memberCount = vo.plan.member_count;
      // 计划ins最大数量
      this.premiumInstagramPublisherCount = vo.plan.premium_instagram_count;
      // 计划fb最大数量
      this.premiumFacebookPublisherCount = vo.plan.premium_facebook_count;
      // 2020.1.14 新增twitter
      this.premiumTwitterPublisherCount = vo.plan.premium_twitter_count;
      // 获取国际化中 年 和 月的文案
      const monthStr = Vue.i18n.translate(
        "myPlanPage.accountInfo.planNextTypeMonth"
      );
      const yearStr = Vue.i18n.translate(
        "myPlanPage.accountInfo.planNextTypeYear"
      );
      // 计算下次用户重置是年还是月
      const planNextType = vo.plan.billing_interval === 1 ? monthStr : yearStr;
      const startDate = dateFormat({
        date: moment(vo.current_period_end),
        format: "MMM DD, YYYY",
      });
      this.nextStartTimeStr = Vue.i18n.translate(
        "myPlanPage.accountInfo.planNextStart",
        {
          type: planNextType,
          date: startDate,
        }
      );
      this.nextStartTime = moment(vo.current_period_end);
      /**
       * 计算下次重置时间
       */
      // 如果付费周期是月，则下次重置unlimited时间就是next start time时间，年的话重新计算
      if (vo.plan.billing_interval === 1) {
        this.nextLimitTime = this.nextStartTime.clone();
      } else {
        // 判断下次解除限制的时间 (下个付费周期的第一天)
        const nextDate = this.nextStartTime.date(); // 获取下次续费的日期
        let monthDiff = 0; // 0 本月 1 下个月
        // 先计算是本月续费还是下月续费
        if (moment().date() > nextDate) {
          // 如果当前日期 大于 上次缴费日期则说明是本月续费
          monthDiff = 0;
        } else {
          monthDiff = 1;
        }
        // 判断下个周期的月的日期是否合法，防止下次续费是31号，但是下周周期的月只有28天
        const nextLimitMonth = moment().add(monthDiff, "M");
        if (nextLimitMonth.daysInMonth() >= nextDate) {
          // 下个周期的时间是合法的
          this.nextLimitTime = nextLimitMonth.date(nextDate);
        } else {
          // 如果不合法则直接取最后一天
          this.nextLimitTime = nextLimitMonth.date(
            nextLimitMonth.daysInMonth()
          );
        }
      }
      this.nextLimitTime = this.nextLimitTime.add(1, "days");
      // 点击取消时间 如果为空说明没有取消
      this.cancelTime = vo.cancel_time;
      // 计划取消时间截止时间
      this.currentPeriodEnd = vo.current_period_end;
      // 当前计划名称 包含年付 月付 信息
      this.nameWithInterval = vo.plan.name_with_interval;
      // 计划持续几月
      this.billingInterval = Number(vo.plan.billing_interval);
      // 2018.12.27 是否使用优惠码
      this.useCoupon = !!vo.coupon_percentage_off;
      // 2019.12.24 新增订阅级别
      this.level = vo.plan.level;
      // 2020.3.29 新增取消订阅相关信息
      // 计算原价
      const regularPrice = vo.plan.price;
      this.cancelInfo = {
        desc: vo.plan.desc, // 原订阅信息描述
        save: "", // 节省的价格
        regular: "", // 原价
      };
      let currency = vo.plan.currency;
      // 2020.30 新增折扣比例 如果为0.3代表优惠的部分 等同于7折
      let discount = 0.7;
      this.cancelInfo.discount = `${NP.times(discount, 100)}%`;
      let price = NP.times(regularPrice, NP.minus(1, discount));
      if (currency === "USD") {
        this.cancelInfo.save = `$${NP.times(regularPrice, discount)}`;
        this.cancelInfo.price = `$${price}`;
        this.cancelInfo.regular = `$${regularPrice}`;
      } else {
        this.cancelInfo.save = `${NP.times(
          regularPrice,
          discount
        )} ${currency}`;
        this.cancelInfo.price = `${price} ${currency}`;
        this.cancelInfo.regular = `${regularPrice} ${currency}`;
      }
    } else {
      this.name = "";
      // 计划publisher最大数量
      this.premiumPublisherCount = 0;
      this.price = 0;
      // 计划member最大数量
      this.memberCount = 0;
      // 计算下次重置时间
      this.nextStartTimeStr = ``;
      this.nextStartTime = "";
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/plans.js